import { CSSProperties, RefObject } from 'react'

import { cellKey } from '../../lib/puzzle'
import { CharStatus } from '../../lib/statuses'

const BOARD_SIZE = 5

export type PuzzleBoardProps = {
  solution: string[]
  board: string[][]
  solvedRows: boolean[]
  attempts: number[]
  clueCells: Set<string>
  revealedCells: Set<string>
  feedback: (CharStatus | undefined)[][]
  selectedCell: { row: number; col: number }
  onSelect: (row: number, col: number) => void
  selectedCellRef?: RefObject<HTMLButtonElement>
}

const getCellLabel = ({
  row,
  col,
  letter,
  isSolved,
  isFixedClue,
  isCrossingClue,
  isRevealed,
  feedbackStatus,
  isSelected,
  isEditable,
}: {
  row: number
  col: number
  letter: string
  isSolved: boolean
  isFixedClue: boolean
  isCrossingClue: boolean
  isRevealed: boolean
  feedbackStatus?: CharStatus
  isSelected: boolean
  isEditable: boolean
}) => {
  const descriptions = [
    letter ? `letter ${letter}` : 'empty',
    isSolved ? 'solved' : undefined,
    isFixedClue ? 'fixed clue' : undefined,
    isCrossingClue ? 'crossing clue' : undefined,
    isRevealed ? 'revealed hint' : undefined,
    feedbackStatus ? `${feedbackStatus} feedback` : undefined,
    isSelected ? 'selected' : undefined,
    isEditable ? 'editable' : 'locked',
  ].filter(Boolean)

  return `Row ${row + 1}, column ${col + 1}: ${descriptions.join(', ')}`
}

export const PuzzleBoard = ({
  solution,
  board,
  solvedRows,
  attempts,
  clueCells,
  revealedCells,
  feedback,
  selectedCell,
  onSelect,
  selectedCellRef,
}: PuzzleBoardProps) => (
  <div
    className={`puzzle-board${
      solvedRows.every(Boolean) ? ' puzzle-board--complete' : ''
    }`}
    role="grid"
    aria-label="Word square puzzle"
    aria-rowcount={BOARD_SIZE}
    aria-colcount={BOARD_SIZE}
  >
    {Array.from({ length: BOARD_SIZE }, (_, row) => {
      const isRowSolved = Boolean(solvedRows[row])
      const rowLabelId = `puzzle-row-${row + 1}-label`
      const rowStatusId = `puzzle-row-${row + 1}-status`

      return (
        <div
          className={`puzzle-row${isRowSolved ? ' puzzle-row--solved' : ''}`}
          role="row"
          aria-rowindex={row + 1}
          aria-labelledby={`${rowLabelId} ${rowStatusId}`}
          key={row}
        >
          <div className="puzzle-row-meta" role="presentation">
            <span className="puzzle-row-label" id={rowLabelId}>
              Row {row + 1}
            </span>
            <span className="puzzle-row-status sr-only" id={rowStatusId}>
              {isRowSolved
                ? 'Solved'
                : attempts[row]
                ? `${attempts[row]} ${attempts[row] === 1 ? 'check' : 'checks'}`
                : 'In progress'}
            </span>
          </div>

          <div className="puzzle-row-cells" role="presentation">
            {Array.from({ length: BOARD_SIZE }, (_, col) => {
              const key = cellKey({ row, col })
              const isFixedClue = clueCells.has(key)
              const isCrossingClue = Boolean(solvedRows[col])
              const isRevealed = revealedCells.has(key)
              const isSelected =
                selectedCell.row === row && selectedCell.col === col
              const isEditable = !(
                isRowSolved ||
                isFixedClue ||
                isCrossingClue ||
                isRevealed
              )
              const isActiveSelected = isSelected && isEditable
              const feedbackStatus = isEditable
                ? feedback[row]?.[col]
                : undefined
              const marker = isRowSolved
                ? null
                : feedbackStatus
                ? {
                    kind: feedbackStatus,
                    label:
                      feedbackStatus === 'correct'
                        ? '✓'
                        : feedbackStatus === 'present'
                        ? '↔'
                        : '×',
                  }
                : isRevealed
                ? { kind: 'hint', label: 'H' }
                : isCrossingClue
                ? { kind: 'crossing', label: '+' }
                : isFixedClue
                ? { kind: 'clue', label: 'C' }
                : null
              const answerLetter = solution[row]?.[col] ?? ''
              const boardLetter = board[row]?.[col] ?? ''
              const letter = (
                isEditable ? boardLetter : answerLetter || boardLetter
              ).toUpperCase()
              const classes = [
                'puzzle-cell',
                letter ? 'puzzle-cell--filled' : 'puzzle-cell--empty',
                isRowSolved ? 'puzzle-cell--solved' : undefined,
                isFixedClue ? 'puzzle-cell--fixed-clue' : undefined,
                isCrossingClue ? 'puzzle-cell--crossing-clue' : undefined,
                isRevealed ? 'puzzle-cell--revealed' : undefined,
                feedbackStatus ? 'puzzle-cell--feedback' : undefined,
                feedbackStatus ? `puzzle-cell--${feedbackStatus}` : undefined,
                isActiveSelected ? 'puzzle-cell--selected' : undefined,
                isEditable ? 'puzzle-cell--editable' : 'puzzle-cell--locked',
              ]
                .filter(Boolean)
                .join(' ')
              const motionStyle = {
                '--cell-index': col,
                '--row-index': row,
                '--ripple-index': row + col,
              } as CSSProperties

              return (
                <button
                  className={classes}
                  type="button"
                  role="gridcell"
                  aria-colindex={col + 1}
                  aria-label={getCellLabel({
                    row,
                    col,
                    letter,
                    isSolved: isRowSolved,
                    isFixedClue,
                    isCrossingClue,
                    isRevealed,
                    feedbackStatus,
                    isSelected: isActiveSelected,
                    isEditable,
                  })}
                  aria-selected={isActiveSelected}
                  aria-readonly={!isEditable}
                  tabIndex={isActiveSelected ? 0 : -1}
                  ref={isActiveSelected ? selectedCellRef : undefined}
                  data-row={row}
                  data-col={col}
                  data-feedback={feedbackStatus}
                  style={motionStyle}
                  onClick={() => isEditable && onSelect(row, col)}
                  key={key}
                >
                  <span
                    className="puzzle-cell-letter"
                    aria-hidden="true"
                    key={letter || 'empty'}
                  >
                    {letter}
                  </span>
                  {marker && (
                    <span
                      className={`puzzle-cell-marker puzzle-cell-marker--${marker.kind}`}
                      aria-hidden="true"
                      data-tile-marker={marker.kind}
                    >
                      {marker.label}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )
    })}
  </div>
)
